// Medical Scores Database - Psychiatry and Mental Health Scores
// Comprehensive psychiatric evaluation tools

import {
  Brain,
  Heart,
  AlertTriangle,
  User,
  Scale,
  Activity
} from 'lucide-react';

// ==================== PSYCHIATRIE ====================
export const PSYCHIATRY_SCORES = {
  hamiltonAnxiety: {
    name: 'Échelle d\'anxiété de Hamilton',
    category: 'Psychiatrie',
    icon: Brain,
    description: 'Évaluation de l\'anxiété',
    color: 'indigo',
    fields: [
      { id: 'anxious', label: 'Humeur anxieuse', type: 'select', options: [
        { value: 0, label: '0 - Absente' }, { value: 1, label: '1 - Légère' },
        { value: 2, label: '2 - Modérée' }, { value: 3, label: '3 - Sévère' }, { value: 4, label: '4 - Très sévère' }
      ]},
      { id: 'tension', label: 'Tension', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }, { value: 4, label: '4' }
      ]},
      { id: 'fears', label: 'Peurs', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }, { value: 4, label: '4' }
      ]},
      { id: 'insomnia', label: 'Insomnie', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }, { value: 4, label: '4' }
      ]},
      { id: 'intellectual', label: 'Difficultés intellectuelles', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }, { value: 4, label: '4' }
      ]},
      { id: 'depressed', label: 'Humeur dépressive', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }, { value: 4, label: '4' }
      ]},
      { id: 'somatic_musc', label: 'Symptômes somatiques (musculaires)', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }, { value: 4, label: '4' }
      ]},
      { id: 'somatic_sens', label: 'Symptômes somatiques (sensoriels)', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }, { value: 4, label: '4' }
      ]},
      { id: 'cardiovascular', label: 'Symptômes cardiovasculaires', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }, { value: 4, label: '4' }
      ]},
      { id: 'respiratory', label: 'Symptômes respiratoires', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }, { value: 4, label: '4' }
      ]},
      { id: 'gastrointestinal', label: 'Symptômes gastro-intestinaux', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }, { value: 4, label: '4' }
      ]},
      { id: 'genitourinary', label: 'Symptômes génito-urinaires', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }, { value: 4, label: '4' }
      ]},
      { id: 'autonomic', label: 'Symptômes du SNA', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }, { value: 4, label: '4' }
      ]},
      { id: 'behavior', label: 'Comportement lors entretien', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }, { value: 4, label: '4' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 56 };
    },
    interpret: (score) => {
      if (score < 17) return { level: 'success', text: `Hamilton Anxiété: ${score}/56`, detail: 'Anxiété légère' };
      if (score < 25) return { level: 'warning', text: `Hamilton Anxiété: ${score}/56`, detail: 'Anxiété légère à modérée' };
      if (score < 30) return { level: 'warning', text: `Hamilton Anxiété: ${score}/56`, detail: 'Anxiété modérée à sévère' };
      return { level: 'danger', text: `Hamilton Anxiété: ${score}/56`, detail: 'Anxiété sévère' };
    },
    formula: 'Hamilton Anxiété = Somme 14 items (0-4 chacun)'
  },

  hamiltonDepression: {
    name: 'Échelle de dépression de Hamilton',
    category: 'Psychiatrie',
    icon: Brain,
    description: 'Détection d\'une dépression',
    color: 'indigo',
    fields: [
      { id: 'depressedMood', label: 'Humeur dépressive', type: 'select', options: [
        { value: 0, label: '0 - Absente' }, { value: 1, label: '1 - Légère' },
        { value: 2, label: '2 - Modérée' }, { value: 3, label: '3 - Sévère' }, { value: 4, label: '4 - Très sévère' }
      ]},
      { id: 'guilt', label: 'Sentiments de culpabilité', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }, { value: 4, label: '4' }
      ]},
      { id: 'suicide', label: 'Suicide', type: 'select', options: [
        { value: 0, label: '0 - Absent' }, { value: 1, label: '1 - La vie ne vaut pas la peine' },
        { value: 2, label: '2 - Souhaite être mort' }, { value: 3, label: '3 - Idées/gestes suicidaires' }, { value: 4, label: '4 - Tentative sérieuse' }
      ]},
      { id: 'insomnia_early', label: 'Insomnie début de nuit', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }
      ]},
      { id: 'insomnia_middle', label: 'Insomnie milieu de nuit', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }
      ]},
      { id: 'insomnia_late', label: 'Insomnie fin de nuit', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }
      ]},
      { id: 'work', label: 'Travail et activités', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }, { value: 4, label: '4' }
      ]},
      { id: 'retardation', label: 'Ralentissement', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }, { value: 4, label: '4' }
      ]},
      { id: 'agitation', label: 'Agitation', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }, { value: 4, label: '4' }
      ]},
      { id: 'anxiety_psychic', label: 'Anxiété psychique', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }, { value: 4, label: '4' }
      ]},
      { id: 'anxiety_somatic', label: 'Anxiété somatique', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }, { value: 4, label: '4' }
      ]},
      { id: 'somatic_gastro', label: 'Symptômes somatiques gastro-intestinaux', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }
      ]},
      { id: 'somatic_general', label: 'Symptômes somatiques généraux', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }
      ]},
      { id: 'genital', label: 'Symptômes génitaux', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }
      ]},
      { id: 'hypochondriasis', label: 'Hypocondrie', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }, { value: 4, label: '4' }
      ]},
      { id: 'weight', label: 'Perte de poids', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }
      ]},
      { id: 'insight', label: 'Conscience de la maladie', type: 'select', options: [
        { value: 0, label: '0 - Reconnaît être déprimé' }, { value: 1, label: '1 - Reconnaît mais attribue à autre chose' }, { value: 2, label: '2 - Nie être malade' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 52 };
    },
    interpret: (score) => {
      if (score < 7) return { level: 'success', text: `HDRS: ${score}/52`, detail: 'Pas de dépression' };
      if (score < 17) return { level: 'warning', text: `HDRS: ${score}/52`, detail: 'Dépression légère' };
      if (score < 24) return { level: 'warning', text: `HDRS: ${score}/52`, detail: 'Dépression modérée' };
      return { level: 'danger', text: `HDRS: ${score}/52`, detail: 'Dépression sévère' };
    },
    formula: 'Hamilton Depression Rating Scale (HDRS-17)'
  },

  bdi13Beck: {
    name: 'BDI-13 de Beck (version courte)',
    category: 'Psychiatrie',
    icon: Brain,
    description: 'Évaluation niveau de dépression',
    color: 'indigo',
    fields: [
      { id: 'sadness', label: 'Tristesse', type: 'select', options: [
        { value: 0, label: '0 - Je ne me sens pas triste' },
        { value: 1, label: '1 - Je me sens triste' },
        { value: 2, label: '2 - Je suis tout le temps triste' },
        { value: 3, label: '3 - Je suis si triste que je ne peux le supporter' }
      ]},
      { id: 'pessimism', label: 'Pessimisme', type: 'select', options: [
        { value: 0, label: '0 - Pas découragé pour l\'avenir' },
        { value: 1, label: '1 - Plus découragé qu\'avant' },
        { value: 2, label: '2 - Je n\'attends rien de bon' },
        { value: 3, label: '3 - L\'avenir est sans espoir' }
      ]},
      { id: 'failure', label: 'Échec', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'dissatisfaction', label: 'Insatisfaction', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'guilt', label: 'Culpabilité', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'self_dislike', label: 'Autodépréciation', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'self_harm', label: 'Idées suicidaires', type: 'select', options: [
        { value: 0, label: '0 - Pas d\'idées de suicide' },
        { value: 1, label: '1 - Pensées mais pas de passage à l\'acte' },
        { value: 2, label: '2 - Je voudrais me suicider' },
        { value: 3, label: '3 - Je me suiciderais si j\'en avais l\'occasion' }
      ]},
      { id: 'social_withdrawal', label: 'Retrait social', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'indecision', label: 'Indécision', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'self_image', label: 'Image corporelle', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'work_difficulty', label: 'Difficultés au travail', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'fatigue', label: 'Fatigue', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'appetite', label: 'Perte d\'appétit', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 39 };
    },
    interpret: (score) => {
      if (score < 4) return { level: 'success', text: `BDI-13: ${score}/39`, detail: 'Pas de dépression' };
      if (score < 8) return { level: 'warning', text: `BDI-13: ${score}/39`, detail: 'Dépression légère' };
      if (score < 16) return { level: 'warning', text: `BDI-13: ${score}/39`, detail: 'Dépression modérée' };
      return { level: 'danger', text: `BDI-13: ${score}/39`, detail: 'Dépression sévère' };
    },
    formula: 'BDI-13 = Somme 13 items (0-3)'
  },

  had: {
    name: 'HAD (Hospital Anxiety and Depression)',
    category: 'Psychiatrie',
    icon: Brain,
    description: 'Dépistage troubles anxieux et dépressifs',
    color: 'indigo',
    fields: [
      { id: 'a1', label: 'Je me sens tendu ou énervé', type: 'select', options: [
        { value: 3, label: '3 - La plupart du temps' }, { value: 2, label: '2 - Souvent' },
        { value: 1, label: '1 - De temps en temps' }, { value: 0, label: '0 - Jamais' }
      ]},
      { id: 'd1', label: 'Je prends plaisir aux mêmes choses qu\'avant', type: 'select', options: [
        { value: 0, label: '0 - Oui, tout autant' }, { value: 1, label: '1 - Pas autant' },
        { value: 2, label: '2 - Un peu seulement' }, { value: 3, label: '3 - Presque plus' }
      ]},
      { id: 'a2', label: 'J\'ai une sensation de peur comme si quelque chose allait arriver', type: 'select', options: [
        { value: 3, label: '3 - Oui, très nettement' }, { value: 2, label: '2 - Oui, mais ce n\'est pas grave' },
        { value: 1, label: '1 - Un peu' }, { value: 0, label: '0 - Pas du tout' }
      ]},
      { id: 'd2', label: 'Je ris facilement et vois le bon côté des choses', type: 'select', options: [
        { value: 0, label: '0 - Autant que par le passé' }, { value: 1, label: '1 - Plus autant' },
        { value: 2, label: '2 - Vraiment moins' }, { value: 3, label: '3 - Plus du tout' }
      ]},
      { id: 'a3', label: 'Je me fais du souci', type: 'select', options: [
        { value: 3, label: '3 - Très souvent' }, { value: 2, label: '2 - Assez souvent' },
        { value: 1, label: '1 - Occasionnellement' }, { value: 0, label: '0 - Très occasionnellement' }
      ]},
      { id: 'd3', label: 'Je suis de bonne humeur', type: 'select', options: [
        { value: 3, label: '3 - Jamais' }, { value: 2, label: '2 - Rarement' },
        { value: 1, label: '1 - Assez souvent' }, { value: 0, label: '0 - La plupart du temps' }
      ]},
      { id: 'a4', label: 'Je peux rester tranquillement assis à ne rien faire', type: 'select', options: [
        { value: 0, label: '0 - Oui, quoi qu\'il arrive' }, { value: 1, label: '1 - Oui, en général' },
        { value: 2, label: '2 - Rarement' }, { value: 3, label: '3 - Jamais' }
      ]},
      { id: 'd4', label: 'J\'ai l\'impression de fonctionner au ralenti', type: 'select', options: [
        { value: 3, label: '3 - Presque toujours' }, { value: 2, label: '2 - Très souvent' },
        { value: 1, label: '1 - Parfois' }, { value: 0, label: '0 - Jamais' }
      ]},
      { id: 'a5', label: 'J\'éprouve des sensations de peur avec des picotements', type: 'select', options: [
        { value: 0, label: '0 - Jamais' }, { value: 1, label: '1 - Parfois' },
        { value: 2, label: '2 - Assez souvent' }, { value: 3, label: '3 - Très souvent' }
      ]},
      { id: 'd5', label: 'Je ne m\'intéresse plus à mon apparence', type: 'select', options: [
        { value: 3, label: '3 - Plus du tout' }, { value: 2, label: '2 - Je n\'y fais plus attention' },
        { value: 1, label: '1 - Je n\'y fais plus assez attention' }, { value: 0, label: '0 - J\'y fais attention' }
      ]},
      { id: 'a6', label: 'J\'ai la bougeotte', type: 'select', options: [
        { value: 3, label: '3 - Oui, c\'est tout à fait le cas' }, { value: 2, label: '2 - Un peu' },
        { value: 1, label: '1 - Pas tellement' }, { value: 0, label: '0 - Pas du tout' }
      ]},
      { id: 'd6', label: 'Je me réjouis d\'avance à l\'idée de faire certaines choses', type: 'select', options: [
        { value: 0, label: '0 - Autant qu\'avant' }, { value: 1, label: '1 - Un peu moins' },
        { value: 2, label: '2 - Bien moins' }, { value: 3, label: '3 - Presque jamais' }
      ]},
      { id: 'a7', label: 'J\'éprouve des sensations soudaines de panique', type: 'select', options: [
        { value: 3, label: '3 - Vraiment très souvent' }, { value: 2, label: '2 - Assez souvent' },
        { value: 1, label: '1 - Pas très souvent' }, { value: 0, label: '0 - Jamais' }
      ]},
      { id: 'd7', label: 'Je peux prendre plaisir à un bon livre ou une émission', type: 'select', options: [
        { value: 0, label: '0 - Souvent' }, { value: 1, label: '1 - Parfois' },
        { value: 2, label: '2 - Rarement' }, { value: 3, label: '3 - Très rarement' }
      ]}
    ],
    calculate: (values) => {
      let anxiety = 0;
      let depression = 0;
      ['a1', 'a2', 'a3', 'a4', 'a5', 'a6', 'a7'].forEach(key => {
        anxiety += (values[key] || 0);
      });
      ['d1', 'd2', 'd3', 'd4', 'd5', 'd6', 'd7'].forEach(key => {
        depression += (values[key] || 0);
      });
      return { score: anxiety + depression, max: 42, details: { anxiety, depression } };
    },
    interpret: (score, result) => {
      const a = result?.details?.anxiety || 0;
      const d = result?.details?.depression || 0;
      let interpretA = a < 8 ? 'normale' : (a < 11 ? 'douteuse' : 'certaine');
      let interpretD = d < 8 ? 'normale' : (d < 11 ? 'douteuse' : 'certaine');
      let level = (a >= 11 || d >= 11) ? 'danger' : (a >= 8 || d >= 8) ? 'warning' : 'success';
      return { level, text: `HAD Total: ${score}`, detail: `Anxiété: ${a}/21 (${interpretA}) | Dépression: ${d}/21 (${interpretD})` };
    },
    formula: 'HAD = Anxiété (7 items) + Dépression (7 items). Seuil: ≥11 = certain, 8-10 = douteux'
  },

  beckPierceSuicide: {
    name: 'Échelle d\'intentionnalité suicidaire de Beck',
    category: 'Psychiatrie',
    icon: AlertTriangle,
    description: 'Évaluation risque suicidaire',
    color: 'indigo',
    fields: [
      { id: 'isolation', label: 'Isolement', type: 'select', options: [
        { value: 0, label: '0 - Quelqu\'un présent' }, { value: 1, label: '1 - Quelqu\'un à proximité' }, { value: 2, label: '2 - Personne à proximité' }
      ]},
      { id: 'timing', label: 'Moment choisi', type: 'select', options: [
        { value: 0, label: '0 - Intervention probable' }, { value: 1, label: '1 - Intervention possible' }, { value: 2, label: '2 - Intervention peu probable' }
      ]},
      { id: 'precautions', label: 'Précautions contre découverte', type: 'select', options: [
        { value: 0, label: '0 - Aucune' }, { value: 1, label: '1 - Passives' }, { value: 2, label: '2 - Actives' }
      ]},
      { id: 'helpSeeking', label: 'Recherche d\'aide', type: 'select', options: [
        { value: 0, label: '0 - A prévenu quelqu\'un' }, { value: 1, label: '1 - A contacté mais pas révélé' }, { value: 2, label: '2 - N\'a contacté personne' }
      ]},
      { id: 'finalActs', label: 'Actes finaux', type: 'select', options: [
        { value: 0, label: '0 - Aucun' }, { value: 1, label: '1 - Testament verbal' }, { value: 2, label: '2 - Préparation formelle' }
      ]},
      { id: 'preparation', label: 'Préparation active du geste', type: 'select', options: [
        { value: 0, label: '0 - Aucune' }, { value: 1, label: '1 - Minime' }, { value: 2, label: '2 - Élaborée' }
      ]},
      { id: 'note', label: 'Lettre d\'adieu', type: 'select', options: [
        { value: 0, label: '0 - Aucune' }, { value: 1, label: '1 - Rédigée mais non laissée' }, { value: 2, label: '2 - Présente' }
      ]},
      { id: 'communication', label: 'Communication de l\'intention', type: 'select', options: [
        { value: 0, label: '0 - Pas de révélation' }, { value: 1, label: '1 - Communication ambiguë' }, { value: 2, label: '2 - Révélation non ambiguë' }
      ]},
      { id: 'purpose', label: 'But supposé du geste', type: 'select', options: [
        { value: 0, label: '0 - Manipuler, attirer attention' }, { value: 1, label: '1 - Les deux' }, { value: 2, label: '2 - Mourir' }
      ]},
      { id: 'expectation', label: 'Attente concernant l\'issue', type: 'select', options: [
        { value: 0, label: '0 - Survie certaine' }, { value: 1, label: '1 - Issue incertaine' }, { value: 2, label: '2 - Mort certaine' }
      ]},
      { id: 'lethality', label: 'Conception de la létalité de la méthode', type: 'select', options: [
        { value: 0, label: '0 - Moins mortelle' }, { value: 1, label: '1 - Ne savait pas' }, { value: 2, label: '2 - Plus mortelle' }
      ]},
      { id: 'seriousness', label: 'Gravité du geste', type: 'select', options: [
        { value: 0, label: '0 - Ne pensait pas être grave' }, { value: 1, label: '1 - Incertain' }, { value: 2, label: '2 - Pensait que c\'était sérieux' }
      ]},
      { id: 'ambivalence', label: 'Attitude face à la vie/mort', type: 'select', options: [
        { value: 0, label: '0 - Ne voulait pas mourir' }, { value: 1, label: '1 - Ambivalent' }, { value: 2, label: '2 - Voulait mourir' }
      ]},
      { id: 'reaction', label: 'Réaction au fait d\'être sauvé', type: 'select', options: [
        { value: 0, label: '0 - Content' }, { value: 1, label: '1 - Ambivalent' }, { value: 2, label: '2 - Déçu' }
      ]},
      { id: 'premeditation', label: 'Préméditation', type: 'select', options: [
        { value: 0, label: '0 - Impulsion' }, { value: 1, label: '1 - Envisagé < 1h' }, { value: 2, label: '2 - Envisagé > 1h' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 30 };
    },
    interpret: (score) => {
      if (score < 10) return { level: 'warning', text: `Beck Suicide: ${score}/30`, detail: 'Intentionnalité faible' };
      if (score < 19) return { level: 'danger', text: `Beck Suicide: ${score}/30`, detail: 'Intentionnalité modérée. Surveillance étroite' };
      return { level: 'danger', text: `Beck Suicide: ${score}/30`, detail: 'Intentionnalité élevée. Hospitalisation psychiatrique' };
    },
    formula: 'Beck Suicidal Intent Scale = 15 items × 0-2'
  },

  edinburghPostnatal: {
    name: 'Échelle d\'Édimbourg (EPDS)',
    category: 'Psychiatrie',
    icon: Brain,
    description: 'Dépistage dépression du post-partum',
    color: 'indigo',
    fields: [
      { id: 'q1', label: 'J\'ai pu rire et prendre les choses du bon côté', type: 'select', options: [
        { value: 0, label: '0 - Aussi souvent qu\'avant' }, { value: 1, label: '1 - Pas tout à fait autant' },
        { value: 2, label: '2 - Vraiment moins souvent' }, { value: 3, label: '3 - Jamais' }
      ]},
      { id: 'q2', label: 'Je me suis sentie confiante et joyeuse en pensant à l\'avenir', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'q3', label: 'Je me suis reprochée sans raison d\'être responsable quand les choses allaient mal', type: 'select', options: [
        { value: 3, label: '3 - Oui, la plupart du temps' }, { value: 2, label: '2 - Oui, parfois' },
        { value: 1, label: '1 - Pas très souvent' }, { value: 0, label: '0 - Non, jamais' }
      ]},
      { id: 'q4', label: 'Je me suis sentie inquiète ou soucieuse sans motif', type: 'select', options: [
        { value: 0, label: '0 - Non, pas du tout' }, { value: 1, label: '1 - Presque jamais' },
        { value: 2, label: '2 - Oui, parfois' }, { value: 3, label: '3 - Oui, très souvent' }
      ]},
      { id: 'q5', label: 'Je me suis sentie effrayée ou paniquée sans vraiment de raison', type: 'select', options: [
        { value: 3, label: '3 - Oui, vraiment souvent' }, { value: 2, label: '2 - Oui, parfois' },
        { value: 1, label: '1 - Non, pas très souvent' }, { value: 0, label: '0 - Non, pas du tout' }
      ]},
      { id: 'q6', label: 'J\'ai eu tendance à me sentir dépassée par les événements', type: 'select', options: [
        { value: 3, label: '3 - Oui, la plupart du temps' }, { value: 2, label: '2 - Oui, parfois' },
        { value: 1, label: '1 - Non, j\'ai fait face' }, { value: 0, label: '0 - Non, pas du tout' }
      ]},
      { id: 'q7', label: 'Je me suis sentie si malheureuse que j\'ai eu des problèmes de sommeil', type: 'select', options: [
        { value: 3, label: '3 - Oui, la plupart du temps' }, { value: 2, label: '2 - Oui, parfois' },
        { value: 1, label: '1 - Pas très souvent' }, { value: 0, label: '0 - Non, pas du tout' }
      ]},
      { id: 'q8', label: 'Je me suis sentie triste ou peu heureuse', type: 'select', options: [
        { value: 3, label: '3 - Oui, la plupart du temps' }, { value: 2, label: '2 - Oui, assez souvent' },
        { value: 1, label: '1 - Pas très souvent' }, { value: 0, label: '0 - Non, pas du tout' }
      ]},
      { id: 'q9', label: 'Je me suis sentie si malheureuse que j\'en ai pleuré', type: 'select', options: [
        { value: 3, label: '3 - Oui, la plupart du temps' }, { value: 2, label: '2 - Oui, assez souvent' },
        { value: 1, label: '1 - Seulement de temps en temps' }, { value: 0, label: '0 - Non, jamais' }
      ]},
      { id: 'q10', label: 'Il m\'est arrivé de penser à me faire du mal', type: 'select', options: [
        { value: 3, label: '3 - Oui, assez souvent' }, { value: 2, label: '2 - Parfois' },
        { value: 1, label: '1 - Presque jamais' }, { value: 0, label: '0 - Jamais' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 30 };
    },
    interpret: (score) => {
      if (score < 9) return { level: 'success', text: `EPDS: ${score}/30`, detail: 'Pas de symptômes dépressifs' };
      if (score < 13) return { level: 'warning', text: `EPDS: ${score}/30`, detail: 'Symptômes dépressifs probables. Surveillance' };
      return { level: 'danger', text: `EPDS: ${score}/30`, detail: 'Dépression post-partum probable. Consultation psychiatrique' };
    },
    formula: 'EPDS ≥ 13 = dépression post-partum probable'
  },

  scoff: {
    name: 'Questionnaire SCOFF',
    category: 'Psychiatrie',
    icon: Scale,
    description: 'Dépistage troubles alimentaires',
    color: 'indigo',
    fields: [
      { id: 's', label: 'Vous faites-vous vomir parce que vous vous sentez mal d\'avoir trop mangé?', type: 'select', options: [
        { value: 1, label: 'Oui' }, { value: 0, label: 'Non' }
      ]},
      { id: 'c', label: 'Vous inquiétez-vous d\'avoir perdu le contrôle de ce que vous mangez?', type: 'select', options: [
        { value: 1, label: 'Oui' }, { value: 0, label: 'Non' }
      ]},
      { id: 'o', label: 'Avez-vous récemment perdu plus de 6 kg en 3 mois?', type: 'select', options: [
        { value: 1, label: 'Oui' }, { value: 0, label: 'Non' }
      ]},
      { id: 'f', label: 'Vous trouvez-vous trop grosse alors que les autres vous disent trop maigre?', type: 'select', options: [
        { value: 1, label: 'Oui' }, { value: 0, label: 'Non' }
      ]},
      { id: 'f2', label: 'Diriez-vous que la nourriture domine votre vie?', type: 'select', options: [
        { value: 1, label: 'Oui' }, { value: 0, label: 'Non' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 5 };
    },
    interpret: (score) => {
      if (score < 2) return { level: 'success', text: `SCOFF: ${score}/5`, detail: 'Trouble alimentaire peu probable' };
      return { level: 'danger', text: `SCOFF: ${score}/5`, detail: 'Trouble alimentaire probable. Évaluation spécialisée' };
    },
    formula: 'SCOFF ≥ 2 = trouble alimentaire probable'
  },

  pss: {
    name: 'Perceived Stress Scale (PSS-10)',
    category: 'Psychiatrie',
    icon: Activity,
    description: 'Échelle de stress perçu',
    color: 'indigo',
    fields: [
      { id: 'q1', label: 'Avez-vous été dérangé par un événement inattendu?', type: 'select', options: [
        { value: 0, label: '0 - Jamais' }, { value: 1, label: '1 - Presque jamais' },
        { value: 2, label: '2 - Parfois' }, { value: 3, label: '3 - Assez souvent' }, { value: 4, label: '4 - Très souvent' }
      ]},
      { id: 'q2', label: 'Avez-vous eu le sentiment de ne pas pouvoir contrôler les choses importantes?', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }, { value: 4, label: '4' }
      ]},
      { id: 'q3', label: 'Vous êtes-vous senti nerveux ou stressé?', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }, { value: 4, label: '4' }
      ]},
      { id: 'q4', label: 'Avez-vous eu confiance en votre capacité à gérer vos problèmes? (inversé)', type: 'select', options: [
        { value: 4, label: '0 - Jamais' }, { value: 3, label: '1 - Presque jamais' },
        { value: 2, label: '2 - Parfois' }, { value: 1, label: '3 - Assez souvent' }, { value: 0, label: '4 - Très souvent' }
      ]},
      { id: 'q5', label: 'Avez-vous senti que les choses allaient comme vous le vouliez? (inversé)', type: 'select', options: [
        { value: 4, label: '0 - Jamais' }, { value: 3, label: '1 - Presque jamais' },
        { value: 2, label: '2 - Parfois' }, { value: 1, label: '3 - Assez souvent' }, { value: 0, label: '4 - Très souvent' }
      ]},
      { id: 'q6', label: 'Avez-vous pensé ne pas pouvoir faire face à tout ce que vous aviez à faire?', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }, { value: 4, label: '4' }
      ]},
      { id: 'q7', label: 'Avez-vous été capable de maîtriser votre énervement? (inversé)', type: 'select', options: [
        { value: 4, label: '0 - Jamais' }, { value: 3, label: '1' }, { value: 2, label: '2' }, { value: 1, label: '3' }, { value: 0, label: '4 - Très souvent' }
      ]},
      { id: 'q8', label: 'Avez-vous senti que vous dominiez la situation? (inversé)', type: 'select', options: [
        { value: 4, label: '0 - Jamais' }, { value: 3, label: '1' }, { value: 2, label: '2' }, { value: 1, label: '3' }, { value: 0, label: '4 - Très souvent' }
      ]},
      { id: 'q9', label: 'Avez-vous été irrité par des choses hors de votre contrôle?', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }, { value: 4, label: '4' }
      ]},
      { id: 'q10', label: 'Avez-vous pensé que les difficultés s\'accumulaient?', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }, { value: 4, label: '4' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 40 };
    },
    interpret: (score) => {
      if (score < 14) return { level: 'success', text: `PSS: ${score}/40`, detail: 'Stress faible' };
      if (score < 27) return { level: 'warning', text: `PSS: ${score}/40`, detail: 'Stress modéré' };
      return { level: 'danger', text: `PSS: ${score}/40`, detail: 'Stress élevé. Prise en charge recommandée' };
    },
    formula: 'PSS-10: 10 questions × 0-4'
  }
};

export default PSYCHIATRY_SCORES;
